import { useEffect, useState } from "react";
import ClientLayout from "@/components/ClientLayout";
import api from "@/lib/api";
import { Gift, Copy, Check, Users, Sparkles, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Referral() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { api.get("/referral/me").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <ClientLayout><div className="pulse-organic w-3 h-3 rounded-full bg-sage mx-auto mt-20" /></ClientLayout>;

  const link = `${window.location.origin}/?ref=${data.code}`;
  const shareText = `Je m'entraîne avec MAIRUNCOACH, un coaching running IA + coach humain. Inscris-toi avec mon lien et profite de -${data.referee_percent}% sur ton premier cycle : ${link}`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); toast.success("Lien copié"); setTimeout(() => setCopied(false), 1800); }
    catch { toast.error("Impossible de copier"); }
  };
  const whatsapp = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
  const mail = () => { window.location.href = `mailto:?subject=${encodeURIComponent("Rejoins-moi sur MAIRUNCOACH")}&body=${encodeURIComponent(shareText)}`; };

  return (
    <ClientLayout>
      <div className="fade-up" data-testid="referral-page">
        <span className="inline-flex items-center gap-1.5 text-xs tracking-[0.14em] uppercase font-semibold text-sage"><Gift size={14} /> Parrainage</span>
        <h1 className="mt-4 text-3xl sm:text-4xl font-light tracking-tight">Invite un ami à courir</h1>
        <p className="mt-3 text-obsidian/60 leading-relaxed max-w-lg">
          Ton ami profite de <span className="font-semibold text-sage">-{data.referee_percent}%</span> sur son premier cycle.
          Dès qu'il rejoint une offre, tu reçois <span className="font-semibold text-sage">-{data.referrer_percent}%</span> sur ton prochain cycle.
        </p>

        {!data.active && (
          <div className="mt-6 rounded-xl border border-border bg-white p-5 text-sm text-obsidian/60" data-testid="referral-inactive">
            Le programme de parrainage est actuellement en pause. Reviens bientôt !
          </div>
        )}

        {data.active && (
          <>
            <div className="mt-6 rounded-2xl p-7 border border-sage/20 bg-gradient-to-br from-sage/10 via-brandblue/5 to-transparent" data-testid="referral-link-card">
              <div className="text-xs tracking-[0.14em] uppercase font-semibold text-obsidian/40">Ton lien de parrainage</div>
              <div className="mt-3 flex items-center gap-2">
                <div className="flex-1 min-w-0 rounded-full bg-white border border-border px-4 py-3 text-sm font-mono truncate" data-testid="referral-link">{link}</div>
                <button data-testid="referral-copy" onClick={copy} className="shrink-0 rounded-full bg-obsidian text-white px-4 py-3 text-sm font-medium inline-flex items-center gap-1.5 hover:-translate-y-0.5 transition-transform">
                  {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copié" : "Copier"}
                </button>
              </div>
              <div className="mt-4 flex flex-wrap gap-2.5">
                <button data-testid="referral-whatsapp" onClick={whatsapp} className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-transform">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.599 5.393l-.999 3.648 3.9-1.34zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.767.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
                  WhatsApp
                </button>
                <button data-testid="referral-mail" onClick={mail} className="inline-flex items-center gap-2 rounded-full bg-brandblue text-white px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-transform">
                  <Mail size={16} /> Email
                </button>
              </div>
            </div>

            {data.credit_percent > 0 && (
              <div className="mt-4 rounded-xl border border-sage/30 bg-sage/5 px-4 py-3 text-sm text-obsidian/75 inline-flex items-center gap-2" data-testid="referral-credit">
                <Sparkles size={15} className="text-sage" /> Tu as une réduction de <span className="font-semibold">-{data.credit_percent}%</span> à utiliser sur ton prochain paiement.
              </div>
            )}

            <div className="mt-8 grid grid-cols-2 gap-3 max-w-sm">
              <div className="rounded-xl border border-border bg-white p-5" data-testid="referral-stat-invited">
                <div className="flex items-center gap-2 text-obsidian/40"><Users size={15} /><span className="text-xs tracking-wide uppercase font-semibold">Invités</span></div>
                <div className="mt-1 font-display text-3xl font-medium tabnum">{data.invited}</div>
              </div>
              <div className="rounded-xl border border-border bg-white p-5" data-testid="referral-stat-rewarded">
                <div className="flex items-center gap-2 text-obsidian/40"><Gift size={15} /><span className="text-xs tracking-wide uppercase font-semibold">Récompensés</span></div>
                <div className="mt-1 font-display text-3xl font-medium tabnum text-sage">{data.rewarded}</div>
              </div>
            </div>

            {data.referrals.length > 0 && (
              <div className="mt-8">
                <h2 className="text-lg font-medium">Mes parrainages</h2>
                <div className="mt-3 space-y-2">
                  {data.referrals.map((r, i) => (
                    <div key={i} data-testid={`referral-row-${i}`} className="rounded-xl border border-border bg-white px-4 py-3 flex items-center justify-between">
                      <span className="text-sm">{r.name}</span>
                      <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${r.status === "rewarded" ? "bg-sage/15 text-sage" : "bg-secondary text-obsidian/50"}`}>
                        {r.status === "rewarded" ? "Récompensé" : "En attente du 1er achat"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </ClientLayout>
  );
}
